import type { CannedScenario } from "../data/types";

interface Props {
  scenarios: CannedScenario[];
  resolvedIds: Set<string>;
  onSelect: (idx: number) => void;
  onClearResolved: () => void;
}

const SEVERITY_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

export function Queue({ scenarios, resolvedIds, onSelect, onClearResolved }: Props) {
  // Partition into active vs resolved, sort active by severity then by age (newer first)
  const indexed = scenarios.map((s, i) => ({ s, i }));
  const active = indexed
    .filter(({ s }) => !resolvedIds.has(s.incident.incident_id))
    .sort((a, b) => {
      const sa = SEVERITY_RANK[a.s.triage_card.severity] ?? 9;
      const sb = SEVERITY_RANK[b.s.triage_card.severity] ?? 9;
      if (sa !== sb) return sa - sb;
      // Keep authoring order (which is recency)
      return a.i - b.i;
    });
  const resolved = indexed.filter(({ s }) => resolvedIds.has(s.incident.incident_id));

  return (
    <div className="queue">
      <div className="queue-header">
        <div>
          <h1>Open incidents</h1>
          <p className="muted">
            {active.length} open · {resolved.length} resolved this session
          </p>
        </div>
        {resolved.length > 0 && (
          <button type="button" className="button ghost sm" onClick={onClearResolved}>
            Reopen all
          </button>
        )}
      </div>

      <div className="queue-list">
        {active.map(({ s, i }) => (
          <QueueRow key={s.incident.incident_id} s={s} resolved={false} onClick={() => onSelect(i)} />
        ))}
        {resolved.length > 0 && active.length > 0 && <div className="queue-divider">Resolved</div>}
        {resolved.map(({ s, i }) => (
          <QueueRow key={s.incident.incident_id} s={s} resolved={true} onClick={() => onSelect(i)} />
        ))}
      </div>
    </div>
  );
}

function QueueRow({
  s,
  resolved,
  onClick,
}: {
  s: CannedScenario;
  resolved: boolean;
  onClick: () => void;
}) {
  const sev = s.triage_card.severity.toLowerCase();
  return (
    <button
      type="button"
      className={`queue-row ${resolved ? "resolved" : ""}`}
      onClick={onClick}
      disabled={resolved}
      aria-label={`${s.triage_card.severity} ${s.incident.title}`}
    >
      <span className={`sev-stripe ${sev}`} />
      <span className={`badge severity-${sev}`}>{s.triage_card.severity}</span>
      <span className="mono incident-id">{s.incident.incident_id.slice(-8)}</span>
      <span className="title">{s.incident.title}</span>
      <span className="source-pill">{s.incident.source.toUpperCase()}</span>
      <span className="age">{resolved ? "resolved" : s.incident.received_at_human}</span>
    </button>
  );
}
